/**
 * The only stateful piece of the engine. Wraps pure `reduce` with
 * subscriptions so hosts can react to events without the reducer knowing
 * a host exists.
 */
import { cloneWorldState } from "./cloneWorldState.ts";
import { deserializeWorldState } from "./deserializeWorldState.ts";
import { freezeWorldState } from "./freezeWorldState.ts";
import type { Command } from "./models/commands.ts";
import type { DomainEvent, DomainEventType } from "./models/events.ts";
import type { WorldBalance } from "./models/config/worldBalance.ts";
import { DEFAULT_WORLD_BALANCE } from "./models/config/worldBalance.ts";
import { tileKey, type TileTerrain, type WorldState } from "./models/worldState.ts";
import { createInitialWorld, type CreateInitialWorldOptions } from "./reducers/factory/createInitialWorld.ts";
import { revealTile } from "./reducers/map/tileVisibility.ts";
import { reduce } from "./reducers/reduce.ts";
import type { LocaleId, ReduceContext } from "./reducers/context.ts";
import type { WorldMapScenario } from "./reducers/map/mapDefinition.ts";

export type StateListener = (state: WorldState) => void;
export type EventBatchListener = (events: readonly DomainEvent[]) => void;
export type EventHandler<T extends DomainEventType> = (
  event: Extract<DomainEvent, { type: T }>,
) => void;
export type Unsubscribe = () => void;
export type ListenerErrorHandler = (error: unknown) => void;

export interface CreateGameOptions extends CreateInitialWorldOptions {
  readonly seed?: number;
  readonly locale?: LocaleId;
  readonly mapScenario?: WorldMapScenario | null;
  readonly timelineStrings?: ReduceContext["timelineStrings"];
  readonly autoNotifications?: boolean;
  /** Resume from a validated world snapshot instead of generating a new one. */
  readonly initial?: WorldState;
  readonly onListenerError?: ListenerErrorHandler;
}

export interface GameStore {
  getState: () => WorldState;
  dispatch: (command: Command) => readonly DomainEvent[];
  subscribe: (listener: StateListener) => Unsubscribe;
  on<T extends DomainEventType>(type: T, handler: EventHandler<T>): Unsubscribe;
  on(handler: EventBatchListener): Unsubscribe;
  getMapScenario: () => WorldMapScenario | null;
  setMapScenario: (mapScenario: WorldMapScenario | null) => void;
  setLocale: (locale: LocaleId) => void;
  serialize: () => string;
  /** Dev/e2e only — mutates clan population without going through reduce. */
  setClanPopulationForTest: (population: number) => void;
  /** Dev/e2e only — mutates tile terrain without going through reduce. */
  setTileTerrainForTest: (q: number, r: number, terrain: TileTerrain) => void;
  /** Dev/e2e only — mutates tile food depletion without going through reduce. */
  setTileFoodDepletionForTest: (q: number, r: number, foodDepletion: number) => void;
  /** Dev/e2e only — reveals tiles without going through reduce. */
  revealTilesForTest: (tiles: readonly { q: number; r: number }[]) => void;
}

export function createGame(options: CreateGameOptions = {}): GameStore {
  const {
    seed = 1,
    locale = "en",
    balance = DEFAULT_WORLD_BALANCE,
    tiles,
    mapScenario = null,
    timelineStrings,
    autoNotifications,
    initial,
    onListenerError,
  } = options;

  let state = freezeWorldState(
    initial ?? createInitialWorld(seed, { balance, tiles }),
  );
  let currentLocale: LocaleId = locale;
  let currentMapScenario: WorldMapScenario | null = mapScenario;
  const stateListeners = new Set<StateListener>();
  const eventListeners = new Set<EventBatchListener>();
  let notifying = false;
  const pendingCommands: Command[] = [];

  const reduceContext = (): ReduceContext => ({
    locale: currentLocale,
    balance,
    mapScenario: currentMapScenario,
    timelineStrings,
    autoNotifications,
  });

  const runDispatch = (command: Command): readonly DomainEvent[] => {
    const result = reduce(cloneWorldState(state), command, reduceContext());
    state = freezeWorldState(result.state);
    notifying = true;
    try {
      for (const listener of eventListeners) {
        try {
          listener(result.events);
        } catch (error) {
          reportListenerError(error, onListenerError);
        }
      }
      for (const listener of stateListeners) {
        try {
          listener(state);
        } catch (error) {
          reportListenerError(error, onListenerError);
        }
      }
    } finally {
      notifying = false;
    }
    return result.events;
  };

  const flushPendingCommands = (): void => {
    while (pendingCommands.length > 0) {
      const next = pendingCommands.shift()!;
      runDispatch(next);
    }
  };

  const dispatch = (command: Command): readonly DomainEvent[] => {
    if (notifying) {
      pendingCommands.push(command);
      return [];
    }

    const events = runDispatch(command);
    flushPendingCommands();
    return events;
  };

  const subscribe = (listener: StateListener): Unsubscribe => {
    stateListeners.add(listener);
    return () => {
      stateListeners.delete(listener);
    };
  };

  const subscribeEvents = (listener: EventBatchListener): Unsubscribe => {
    eventListeners.add(listener);
    return () => {
      eventListeners.delete(listener);
    };
  };

  function on<T extends DomainEventType>(
    type: T,
    handler: EventHandler<T>,
  ): Unsubscribe;
  
  function on(handler: EventBatchListener): Unsubscribe;
  
  function on(
    typeOrHandler: DomainEventType | EventBatchListener,
    handler?: EventHandler<DomainEventType>,
  ): Unsubscribe {
    if (typeof typeOrHandler === "function") {
      return subscribeEvents(typeOrHandler);
    }
    const type = typeOrHandler;
    return subscribeEvents((events) => {
      for (const event of events) {
        if (event.type === type) {
          handler!(event as Extract<DomainEvent, { type: typeof type }>);
        }
      }
    });
  }

  const notifyStateOnly = (): void => {
    for (const listener of stateListeners) {
      try {
        listener(state);
      } catch (error) {
        reportListenerError(error, onListenerError);
      }
    }
  };

  const setClanPopulationForTest = (population: number): void => {
    const next = cloneWorldState(state);
    next.clan.population = population;
    state = freezeWorldState(next);
    notifyStateOnly();
  };

  const setTileTerrainForTest = (
    q: number,
    r: number,
    terrain: TileTerrain,
  ): void => {
    const key = tileKey(q, r);
    const next = cloneWorldState(state);
    const tile = next.tiles[key];
    if (!tile) {
      return;
    }
    next.tiles[key] = { ...tile, terrain };
    state = freezeWorldState(next);
    notifyStateOnly();
  };

  const setTileFoodDepletionForTest = (
    q: number,
    r: number,
    foodDepletion: number,
  ): void => {
    const key = tileKey(q, r);
    const next = cloneWorldState(state);
    const tile = next.tiles[key];
    if (!tile) {
      return;
    }
    next.tiles[key] = {
      ...tile,
      foodDepletion: Math.min(1, Math.max(0, foodDepletion)),
    };
    state = freezeWorldState(next);
    notifyStateOnly();
  };

  const revealTilesForTest = (
    tiles: readonly { q: number; r: number }[],
  ): void => {
    const next = cloneWorldState(state);
    let changed = false;
    for (const { q, r } of tiles) {
      const key = tileKey(q, r);
      const tile = next.tiles[key];
      if (!tile) {
        continue;
      }
      const revealed = revealTile(tile);
      if (revealed !== tile) {
        next.tiles[key] = revealed;
        changed = true;
      }
    }
    if (!changed) {
      return;
    }
    state = freezeWorldState(next);
    notifyStateOnly();
  };

  return {
    getState: () => state,
    dispatch,
    subscribe,
    on,
    getMapScenario: () => currentMapScenario,
    setMapScenario: (next) => {
      currentMapScenario = next;
    },
    setLocale: (next) => {
      currentLocale = next;
    },
    serialize: () => JSON.stringify(state),
    setClanPopulationForTest,
    setTileTerrainForTest,
    setTileFoodDepletionForTest,
    revealTilesForTest,
  };
}

export function deserializeGame(
  json: string,
  options: Omit<CreateGameOptions, "initial" | "seed" | "tiles"> = {},
): GameStore | null {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }

  const state = deserializeWorldState(raw);
  if (state === null) {
    return null;
  }

  return createGame({ ...options, initial: state });
}

function reportListenerError(
  error: unknown,
  onListenerError?: ListenerErrorHandler,
): void {
  try {
    onListenerError?.(error);
  } catch {
    // Error reporting must not block remaining listeners either.
  }
}

export type { WorldBalance };
